import React from "react";
import { Button, Modal, Table } from "react-bootstrap";

import * as structureSet from "../../../dicom/structure-set";
import { compareStrayContourPositions, findAdjacentByPosition, getStrayContourPosition, StrayContour, StrayContourKind, StrayContourPosition } from "../../../dicom/stray-contours";
import ModalDialog from "../../common/ModalDialog";

import './StrayContoursDialog.css';


export type RoiStrayContours = {
    roi: structureSet.Roi,
    contours: StrayContour[],
};

export type StrayContourFinding = { roi: structureSet.Roi, contour: StrayContour, position: StrayContourPosition };

type OwnProps = {
    results: RoiStrayContours[] | null,
    /** The finding being shown in the viewer, or null to show the list. */
    shown: StrayContourFinding | null,
    onShow: (finding: StrayContourFinding | null) => void,
    onStep: (direction: 1 | -1) => void,
    onClose: () => void,
};

const kindDescriptions: { [kind in StrayContourKind]: string } = {
    [StrayContourKind.Degenerate]: 'Less than 4 points (not drawn)',
    [StrayContourKind.UnmatchedSlice]: 'Not on any image slice (not drawn)',
    [StrayContourKind.Tiny]: 'Tiny',
    [StrayContourKind.Isolated]: 'Detached from rest of structure',
};

export function getStrayContourFindings(results: RoiStrayContours[]): StrayContourFinding[] {
    return results.reduce((acc, r, structureOrder) => acc.concat(r.contours.map(contour =>
        ({ roi: r.roi, contour, position: getStrayContourPosition(structureOrder, contour) }))), [] as StrayContourFinding[]);
}

/** Findings that can be located in the viewer */
export function canShowStrayContour(contour: StrayContour): boolean {
    return contour.sliceIndex >= 0 && contour.pointCount > 0;
}

/** The next (direction 1) or previous (direction -1) finding that can be shown, relative to the given position. */
export function findAdjacentStrayContour(results: RoiStrayContours[], current: StrayContourPosition, direction: 1 | -1): StrayContourFinding | null {
    const showable = getStrayContourFindings(results).filter(f => canShowStrayContour(f.contour));
    return findAdjacentByPosition(showable, current, direction);
}

const formatSlice = (c: StrayContour) => c.sliceIndex >= 0 ? c.sliceIndex.toString() : c.sliceId;
const formatSize = (c: StrayContour) => `${c.sizeMm[0].toFixed(1)} × ${c.sizeMm[1].toFixed(1)}`;

class StrayContoursDialog extends React.PureComponent<OwnProps> {

    renderList(findings: StrayContourFinding[], structureCount: number) {
        return (
            <ModalDialog size='lg' show={true} onHide={this.props.onClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Stray contours</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Contours as they would be saved now, including unsaved edits.</p>
                    {findings.length === 0 ? (
                        <p>No stray contours found in {structureCount} structure(s).</p>
                    ) : (
                        <Table size="sm" striped>
                            <thead>
                                <tr><th>Structure</th><th>Slice</th><th>Issue</th><th>Points</th><th>Area (mm²)</th><th>Size (mm)</th><th /></tr>
                            </thead>
                            <tbody>
                                {findings.map((finding, i) => {
                                    const c = finding.contour;
                                    return (
                                        <tr key={i}>
                                            <td>{finding.roi.name}</td>
                                            <td>{formatSlice(c)}</td>
                                            <td>{kindDescriptions[c.kind]}</td>
                                            <td>{c.pointCount}</td>
                                            <td>{c.areaMm2.toFixed(1)}</td>
                                            <td>{formatSize(c)}</td>
                                            <td>
                                                {canShowStrayContour(c) && (
                                                    <Button size="sm" variant="outline-primary" onClick={() => this.props.onShow(finding)}>Show</Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={this.props.onClose}>Close</Button>
                </Modal.Footer>
            </ModalDialog>
        );
    }

    /**
     * Non-modal bar for stepping through the findings while the viewer (and its crosshair) stays visible.
     * The findings are recalculated when stepping, so edits made meanwhile are taken into account.
     */
    renderNavigator(results: RoiStrayContours[], shown: StrayContourFinding) {
        const showable = getStrayContourFindings(results).filter(f => canShowStrayContour(f.contour));
        const position = showable.findIndex(f => compareStrayContourPositions(f.position, shown.position) === 0);
        const c = shown.contour;
        const counter = `${position >= 0 ? position + 1 : '–'} / ${showable.length}`;
        const description = `${shown.roi.name}, slice ${formatSlice(c)}, ${kindDescriptions[c.kind].toLowerCase()}`
            + ` (${c.pointCount} points, ${c.areaMm2.toFixed(1)} mm², ${formatSize(c)} mm) at crosshair`;

        return (
            <div className="stray-contour-navigator">
                <span className="stray-contour-navigator-text" title={`${counter}: ${description}`}>
                    <b>{counter}</b>: {description}
                </span>
                <Button size="sm" variant="outline-primary" disabled={!findAdjacentStrayContour(results, shown.position, -1)} onClick={() => this.props.onStep(-1)}>‹ Prev</Button>
                <Button size="sm" variant="outline-primary" disabled={!findAdjacentStrayContour(results, shown.position, 1)} onClick={() => this.props.onStep(1)}>Next ›</Button>
                <Button size="sm" variant="outline-primary" onClick={() => this.props.onShow(null)}>Back to list</Button>
                <Button size="sm" variant="outline-primary" onClick={this.props.onClose}>Close</Button>
            </div>
        );
    }

    render() {
        const { results, shown } = this.props;
        if (!results) { return null; }

        return shown
            ? this.renderNavigator(results, shown)
            : this.renderList(getStrayContourFindings(results), results.length);
    }
}

export default StrayContoursDialog;
